import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { getNavItemsByRole, UserRole, NavItem, isGlobalSuperAdmin } from "@/lib/permissions";
import { PaymentPrompt } from "@/components/PaymentPrompt";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { notificationsApi } from "@/lib/api/notifications";
import { useTransferCart } from "@/contexts/TransferCartContext";
import { useActiveBranches } from "@/hooks/useActiveBranches";
import { TransferQueueDrawer } from "@/components/transfers/TransferQueueDrawer";
import { ScrollToTopFab } from "@/components/navigation/ScrollToTopFab";
import { SidebarBrand } from "@/components/layout/SidebarBrand";
import { SidebarNavTree } from "@/components/layout/SidebarNavTree";
import { collectOpenAncestorIds } from "@/components/layout/sidebarNavUtils";
import { documentationSidebarRoot } from "@/pages/documentation/documentationSidebarNav";
import * as Icons from "lucide-react";

interface AppLayoutProps {
  children: React.ReactNode;
}

interface MenuGroup {
  label: string;
  items: NavItem[];
}

export function AppLayout({ children }: AppLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openSubmenuIds, setOpenSubmenuIds] = useState<Set<string>>(() => new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [notificationCount, setNotificationCount] = useState(0);
  const [helpPanelOpen, setHelpPanelOpen] = useState(false);
  const [transferDrawerOpen, setTransferDrawerOpen] = useState(false);
  const [trialBannerDismissed, setTrialBannerDismissed] = useState(false);
  const [paymentBlocked, setPaymentBlocked] = useState(false);
  const { count: transferCartCount } = useTransferCart();
  const { user, logout } = useAuth();
  const { tenant, isMainPlatform: _isMainPlatformS } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine user role
  const getUserRole = (): UserRole | undefined => {
    if (!user) return undefined;
    if (isGlobalSuperAdmin(user)) return "SUPER_ADMIN";

    // Staff members authenticated via staff login
    if (user.isStaff) {
      const staffRoleName = user.roleName?.toUpperCase()?.replace(/\s+/g, "_");
      if (staffRoleName?.includes("TENANT") && staffRoleName?.includes("ADMIN"))
        return "TENANT_ADMIN";
      if (staffRoleName === "LIBRARIAN") return "LIBRARIAN";
      return "STAFF";
    }

    // Regular user - check memberships
    const activeMembership = user.memberships?.[0];
    if (!activeMembership) return undefined;

    const roleName =
      activeMembership.roleName?.toUpperCase() || activeMembership.role?.toUpperCase();
    if (roleName === "TENANT ADMIN" || roleName === "TENANT_ADMIN") return "TENANT_ADMIN";
    if (roleName === "LIBRARIAN") return "LIBRARIAN";
    if (roleName === "STUDENT" || roleName === "PATRON") return "PATRON";

    return undefined;
  };

  const userRole = getUserRole();

  const isOnPaymentSafePage =
    location.pathname === "/" ||
    location.pathname.startsWith("/invoices") ||
    location.pathname.startsWith("/billing") ||
    location.pathname.startsWith("/payment/callback") ||
    location.pathname.startsWith("/paid-resources");

  useEffect(() => {
    if (user && !isGlobalSuperAdmin(user) && userRole === "PATRON" && !isOnPaymentSafePage) {
      import("@/lib/api/registration").then(({ registrationApi }) => {
        registrationApi
          .checkPaymentStatus()
          .then((status) => {
            setPaymentBlocked(status.paymentRequired && !status.paymentCompleted);
          })
          .catch(() => {
            setPaymentBlocked(false);
          });
      });
    } else {
      setPaymentBlocked(false);
    }
  }, [user, userRole, location.pathname]);

  useEffect(() => {
    if (!user) {
      setNotificationCount(0);
      return;
    }
    let cancelled = false;
    notificationsApi
      .listMine()
      .then((list) => {
        if (!cancelled) setNotificationCount(list.filter((n) => !n.readAt).length);
      })
      .catch(() => {
        if (!cancelled) setNotificationCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [user, location.pathname]);

  const trialEndDate = tenant?.trialEndDate ? new Date(tenant.trialEndDate) : null;
  const trialEnded = trialEndDate ? new Date() > trialEndDate : false;
  const isOnTrial = tenant?.status === "trial" && !trialEnded;
  const daysRemaining =
    isOnTrial && trialEndDate
      ? Math.ceil((trialEndDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
      : null;

  // Load branches for the transfer queue drawer (staff and tenant admins)
  const { data: allBranches = [] } = useActiveBranches();
  const permissionsKey = user?.permissions?.join("\u0001") ?? "";
  const navItems = useMemo(
    () => getNavItemsByRole(userRole, user?.permissions || [], user?.isStaff || false),
    [userRole, user?.isStaff, permissionsKey]
  );

  // Group navigation items into logical sections
  const groupNavItems = (items: NavItem[]): MenuGroup[] => {
    const groups: MenuGroup[] = [];

    if (userRole === "SUPER_ADMIN") {
      groups.push({ label: "Administration", items: items });
    } else if (userRole === "TENANT_ADMIN" || userRole === "LIBRARIAN" || userRole === "STAFF") {
      // Main section (dashboard + catalog)
      const mainItems = items.filter(
        (item) =>
          item.id === "dashboard" ||
          item.id === "staff-dashboard" ||
          item.id === "catalog-management" ||
          item.id === "catalog"
      );
      if (mainItems.length > 0) {
        groups.push({ label: "Main", items: mainItems });
      }

      // Operations section (circulation, fines, invoices, ILL, inventory, events, etc.)
      const operationsItems = items.filter(
        (item) =>
          item.id === "circulation" ||
          item.id === "fines" ||
          item.id === "manage-invoices" ||
          item.id === "ill" ||
          item.id === "inventory" ||
          item.id === "course-reserves" ||
          item.id === "events" ||
          item.id === "digital-resources" ||
          item.id === "serials" ||
          item.id === "bindery" ||
          item.id === "thesis"
      );
      if (operationsItems.length > 0) {
        groups.push({ label: "Operations", items: operationsItems });
      }

      // Management section (patrons, acquisitions, notifications, support, reports)
      const managementItems = items.filter(
        (item) =>
          item.id === "patron-management" ||
          item.id === "acquisitions" ||
          item.id === "notifications-management" ||
          item.id === "support-inbox" ||
          item.id === "reports"
      );
      if (managementItems.length > 0) {
        groups.push({ label: "Management", items: managementItems });
      }

      // Admin section (staff, branches, roles, patron types, settings, security, billing)
      const adminItems = items.filter(
        (item) =>
          item.id === "staff-management" ||
          item.id === "branch-management" ||
          item.id === "branches" ||
          item.id === "roles" ||
          item.id === "patron-types" ||
          item.id === "tenant-setup" ||
          item.id === "security" ||
          item.id === "tenant-billing"
      );
      if (adminItems.length > 0) {
        groups.push({ label: "Administration", items: adminItems });
      }

      // Account section (profile)
      const accountItems = items.filter((item) => item.id === "staff-profile");
      if (accountItems.length > 0) {
        groups.push({ label: "Account", items: accountItems });
      }

      // Help/Support section
      groups.push({
        label: "Support",
        items: [
          documentationSidebarRoot(["TENANT_ADMIN", "LIBRARIAN", "STAFF"]),
          {
            id: "help-center",
            label: "Contact support",
            icon: "HelpCircle",
            path: "/contact",
            requiredRole: ["TENANT_ADMIN", "LIBRARIAN", "STAFF"],
          },
        ],
      });
    } else if (userRole === "PATRON" || userRole === "STUDENT") {
      // Patrons get the new "/support" ticketing experience in a dedicated Help group
      // (see `patron-support` in permissions.ts). The legacy "Contact support" link was
      // retired in favor of an auditable help-desk thread.
      const libraryItems = items.filter((item) => item.id !== "patron-support");
      const patronSupportItem = items.find((item) => item.id === "patron-support");

      groups.push({ label: "Library", items: libraryItems });
      groups.push({
        label: "Help",
        items: [...(patronSupportItem ? [patronSupportItem] : [])],
      });
    } else {
      // Fallback: single group
      groups.push({ label: "Menu", items: items });
    }

    return groups;
  };

  const menuGroups = groupNavItems(navItems);

  const toggleOpenSubmenuId = (id: string) => {
    setOpenSubmenuIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Keep ancestor folders open so the active route stays visible (union with user-expanded ids)
  useEffect(() => {
    const groups = groupNavItems(navItems);
    const roots = groups.flatMap((g) => g.items);
    const required = collectOpenAncestorIds(roots, location.pathname);
    setOpenSubmenuIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const id of required) {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [location.pathname, navItems]);

  // Get header title
  // const headerTitle = user?.isSuperAdmin ? "PhiloLMS Super Admin" : tenant?.name || "PhiloLMS";

  // Get user display name
  const userDisplayName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.email || "User";

  // Handle navigation
  const handleNavClick = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Get icon component by name
  const getIcon = (iconName: string) => {
    const IconComponent = (Icons as any)[iconName] || Icons.Circle;
    return IconComponent;
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] print:min-h-0 print:bg-white">
      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm print:hidden lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 z-50 h-full bg-white border-r border-gray-200 transition-all duration-300 ease-in-out flex flex-col print:hidden",
          sidebarCollapsed ? "w-20 overflow-visible" : "w-64 overflow-hidden",
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* Logo/Brand */}
        <div
          className={cn(
            "flex bg-white border-b border-gray-200",
            sidebarCollapsed
              ? "flex-col items-center gap-1 px-2 py-3"
              : "h-16 items-center justify-between gap-2 px-4"
          )}
        >
          <SidebarBrand
            collapsed={sidebarCollapsed}
            logoUrl={tenant?.logoUrl}
            slug={tenant?.slug}
            libraryName={tenant?.name}
          />
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-2 rounded-md hover:bg-[#F8F9FA] transition-colors text-[#495057]"
            aria-label="Toggle sidebar"
          >
            <Icons.Menu className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav
          className={cn(
            "flex-1 py-4 px-3",
            sidebarCollapsed ? "overflow-y-auto overflow-x-visible" : "overflow-y-auto"
          )}
        >
          {menuGroups.map((group, groupIndex) => (
            <div key={group.label} className={cn(groupIndex > 0 && "mt-6")}>
              {!sidebarCollapsed && (
                <div className="px-3 mb-2">
                  <p className="text-xs font-semibold text-[#495057] uppercase tracking-wider">
                    {group.label}
                  </p>
                </div>
              )}
              <SidebarNavTree
                items={group.items}
                depth={0}
                pathname={location.pathname}
                sidebarCollapsed={sidebarCollapsed}
                openIds={openSubmenuIds}
                toggleOpenId={toggleOpenSubmenuId}
                onNavigate={handleNavClick}
                getIcon={getIcon}
              />
            </div>
          ))}
        </nav>

        {/* ── Sidebar billing card (commented out — kept for future use)
        {!sidebarCollapsed &&
          userRole === "TENANT_ADMIN" &&
          user &&
          !isGlobalSuperAdmin(user) &&
          !tenantLoading &&
          tenant && (
            <div className="p-3 border-t border-gray-200">
              {tenant?.status === "trial" ? (
                <Card className="border-0 shadow-sm bg-primary">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2.5">
                      <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-full bg-primary-foreground/20">
                        <Icons.Clock className="w-4 h-4 text-primary-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xs font-semibold text-primary-foreground mb-0.5">
                          Free Trial
                        </h3>
                        <p className="text-[10px] text-primary-foreground/90 mb-1.5">
                          X days left
                        </p>
                        <Button
                          onClick={() => navigate("/billing")}
                          className="w-full bg-white text-primary text-[10px] font-semibold py-1 px-2 rounded-md hover:bg-white/90 transition-colors h-auto"
                        >
                          Upgrade
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-0 shadow-sm bg-primary">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2.5">
                      <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-full bg-primary-foreground/20">
                        <Icons.CheckCircle2 className="w-4 h-4 text-primary-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xs font-semibold text-primary-foreground mb-0.5">
                          Active Plan
                        </h3>
                        <Button
                          onClick={() => navigate("/billing")}
                          variant="outline"
                          className="w-full bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground text-[10px] font-semibold py-1 px-2 rounded-md hover:bg-primary-foreground/20 transition-colors h-auto"
                        >
                          Manage
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        ── End sidebar billing card */}
      </aside>

      {/* Main content area */}
      <div
        className={cn(
          "transition-all duration-300 print:ml-0",
          sidebarCollapsed ? "lg:ml-20" : "lg:ml-64"
        )}
      >
        {/* Header/Navbar */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200 print:hidden">
          <div className="flex items-center justify-between h-16 gap-4 px-4 lg:px-6">
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-md hover:bg-[#F8F9FA] text-[#495057] transition-colors"
              aria-label="Toggle menu"
            >
              <Icons.Menu className="w-5 h-5" />
            </button>

            {/* Search Bar */}
            <div className="flex-1 max-w-md">
              <div className="relative flex items-center gap-2">
                <Icons.Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#495057] opacity-50 pointer-events-none" />
                <Input
                  type="search"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && searchQuery.trim()) {
                      // TODO: Implement search functionality
                      console.log("Searching for:", searchQuery);
                    }
                  }}
                  className="pl-10 pr-24 bg-[#F8F9FA] border-gray-200 focus:bg-white focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                />
                <button
                  onClick={() => {
                    if (searchQuery.trim()) {
                      // TODO: Implement search functionality
                      console.log("Searching for:", searchQuery);
                    }
                  }}
                  className="absolute right-2 bg-primary text-primary-foreground text-sm font-semibold px-4 py-1.5 rounded-md hover:bg-primary/90 transition-colors"
                >
                  Search
                </button>
              </div>
            </div>

            {/* Right side items */}
            <div className="flex items-center gap-1">
              {/* Transfer Queue Button — staff and tenant admins */}
              {(user?.isStaff || userRole === "TENANT_ADMIN") && (
                <button
                  className="relative p-2.5 rounded-lg text-[#495057] transition-all duration-200 hover:bg-primary/10 hover:text-primary hover:scale-105 active:scale-95"
                  aria-label="Transfer Queue"
                  onClick={() => setTransferDrawerOpen(true)}
                  title={
                    transferCartCount > 0
                      ? `${transferCartCount} item${transferCartCount !== 1 ? "s" : ""} in transfer queue`
                      : "Transfer Queue"
                  }
                >
                  <Icons.ArrowRightLeft className="w-5 h-5 transition-transform duration-200" />
                  {transferCartCount > 0 && (
                    <span className="absolute top-0 right-0 w-4 h-4 bg-primary text-primary-foreground text-[10px] font-semibold rounded-full flex items-center justify-center border-2 border-white">
                      {transferCartCount > 9 ? "9+" : transferCartCount}
                    </span>
                  )}
                </button>
              )}

              {/* Notifications */}
              <button
                type="button"
                className="relative p-2.5 rounded-lg text-[#495057] transition-all duration-200 hover:bg-primary/10 hover:text-primary hover:scale-105 active:scale-95"
                aria-label="Notifications"
                onClick={() => navigate("/notifications")}
              >
                <Icons.Bell className="w-5 h-5 transition-transform duration-200" />
                {notificationCount > 0 && (
                  <span className="absolute top-0 right-0 w-4 h-4 bg-[#DC3545] text-white text-[10px] font-semibold rounded-full flex items-center justify-center border-2 border-white animate-pulse">
                    {notificationCount > 9 ? "9+" : notificationCount}
                  </span>
                )}
              </button>

              {/* Help/Support - Desktop */}
              <div className="relative hidden md:block">
                <button
                  className="p-2.5 rounded-lg text-[#495057] transition-all duration-200 hover:bg-primary/10 hover:text-primary hover:scale-105 active:scale-95"
                  aria-label="Help & Support"
                  onClick={() => setHelpPanelOpen(!helpPanelOpen)}
                >
                  <Icons.HelpCircle className="w-5 h-5 transition-transform duration-200" />
                </button>
                {helpPanelOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setHelpPanelOpen(false)} />
                    <div className="absolute right-0 z-50 mt-2 overflow-hidden bg-white border border-gray-100 shadow-xl w-80 rounded-xl">
                      <div className="p-4 bg-primary">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-foreground/20">
                            <Icons.HelpCircle className="w-5 h-5 text-primary-foreground" />
                          </div>
                          <div>
                            <h3 className="text-sm font-semibold text-primary-foreground">
                              Need Help?
                            </h3>
                            <p className="text-xs text-primary-foreground/90">
                              We're here to assist you
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="p-4 space-y-2">
                        <p className="text-xs text-[#495057] opacity-70 mb-2">
                          Browse product guides or contact support for assistance.
                        </p>
                        {(userRole === "TENANT_ADMIN" ||
                          userRole === "LIBRARIAN" ||
                          userRole === "STAFF") && (
                          <Button
                            variant="outline"
                            onClick={() => {
                              setHelpPanelOpen(false);
                              navigate("/internal/user-services-guide");
                            }}
                            className="w-full border-primary/30 text-primary hover:bg-primary/5"
                          >
                            User services guide
                          </Button>
                        )}
                        {(userRole === "TENANT_ADMIN" ||
                          userRole === "LIBRARIAN" ||
                          userRole === "STAFF") && (
                          <Button
                            variant="outline"
                            onClick={() => {
                              setHelpPanelOpen(false);
                              navigate("/documentation");
                            }}
                            className="w-full mb-2 border-primary/30 text-primary hover:bg-primary/5"
                          >
                            Documentation
                          </Button>
                        )}
                        <Button
                          onClick={() => {
                            setHelpPanelOpen(false);
                            navigate(
                              userRole === "PATRON" || userRole === "STUDENT"
                                ? "/support/new"
                                : "/contact"
                            );
                          }}
                          className="w-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                        >
                          {userRole === "PATRON" || userRole === "STUDENT"
                            ? "Open support ticket"
                            : "Contact support"}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* User menu */}
              <div className="relative group">
                <button className="flex items-center gap-2 md:gap-3 p-1.5 md:p-2 rounded-lg transition-all duration-200 hover:bg-primary/10 hover:scale-105 active:scale-95">
                  <div className="flex items-center justify-center text-sm font-semibold transition-transform duration-200 rounded-full shadow-sm w-9 h-9 bg-primary text-primary-foreground group-hover:scale-110">
                    {userDisplayName.charAt(0).toUpperCase()}
                  </div>
                  <div className="hidden text-left md:block">
                    <p className="text-sm font-semibold text-[#495057] transition-colors duration-200 group-hover:text-primary">
                      {userDisplayName}
                    </p>
                    <p className="text-xs text-[#495057] opacity-60">
                      {user?.isStaff && user?.roleName
                        ? user.roleName
                        : userRole?.replace("_", " ") || "User"}
                    </p>
                  </div>
                  <Icons.ChevronDown className="w-4 h-4 text-[#495057] hidden md:block transition-transform duration-200 group-hover:rotate-180" />
                </button>
                {/* Dropdown menu */}
                <div className="absolute right-0 z-50 invisible w-56 mt-2 transition-all duration-200 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 group-hover:visible">
                  <div className="py-2">
                    {/* <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-semibold text-[#495057]">
                        {userDisplayName}
                      </p>
                      <p className="text-xs text-[#495057] opacity-60">
                        {userRole?.replace("_", " ") || "User"}
                      </p>
                    </div> */}
                    <button
                      onClick={() => navigate("/profile")}
                      className="w-full text-left px-4 py-2 text-sm text-[#495057] hover:bg-[#F8F9FA] hover:text-primary transition-colors flex items-center gap-2"
                    >
                      <Icons.User className="w-4 h-4" />
                      Profile
                    </button>
                    <button
                      onClick={() => {
                        if (userRole === "TENANT_ADMIN") {
                          navigate("/setup");
                        } else {
                          navigate("/setup");
                        }
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-[#495057] hover:bg-[#F8F9FA] hover:text-primary transition-colors flex items-center gap-2"
                    >
                      <Icons.Settings className="w-4 h-4" />
                      {userRole === "TENANT_ADMIN" ? "Setup" : "Settings"}
                    </button>
                    {userRole !== "PATRON" && userRole !== "STUDENT" && (
                      <button
                        onClick={() => navigate("/documentation")}
                        className="w-full text-left px-4 py-2 text-sm text-[#495057] hover:bg-[#F8F9FA] hover:text-primary transition-colors flex items-center gap-2"
                      >
                        <Icons.BookOpen className="w-4 h-4" />
                        Documentation
                      </button>
                    )}
                    <button
                      onClick={() => navigate("/contact")}
                      className="w-full text-left px-4 py-2 text-sm text-[#495057] hover:bg-[#F8F9FA] hover:text-primary transition-colors flex items-center gap-2"
                    >
                      <Icons.HelpCircle className="w-4 h-4" />
                      Contact support
                    </button>
                    <hr className="my-1 border-gray-200" />
                    <button
                      onClick={() => {
                        logout();
                        navigate("/login");
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-[#DC3545] hover:bg-red-50 transition-colors flex items-center gap-2"
                    >
                      <Icons.LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Trial mode banner */}
        {isOnTrial && userRole === "TENANT_ADMIN" && !trialBannerDismissed && (
          <div className="border-b bg-amber-50 border-amber-200 print:hidden">
            <div className="flex items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
              <div className="flex items-center min-w-0 gap-3">
                <div className="flex-shrink-0 p-1.5 bg-amber-100 rounded-full">
                  <Icons.Clock className="w-4 h-4 text-amber-600" />
                </div>
                <p className="text-sm text-slate-800">
                  <span className="font-semibold">Free trial</span>
                  {daysRemaining !== null && daysRemaining > 0
                    ? ` — ${daysRemaining} day${daysRemaining !== 1 ? "s" : ""} remaining. `
                    : " — ending soon. "}
                  <button
                    onClick={() => navigate("/billing")}
                    className="font-semibold underline transition-colors underline-offset-2 hover:text-amber-900"
                  >
                    Upgrade now
                  </button>{" "}
                  to keep access to all features.
                </p>
              </div>
              <button
                onClick={() => setTrialBannerDismissed(true)}
                className="flex-shrink-0 p-1 transition-colors rounded-md hover:bg-amber-100 text-amber-500 hover:text-amber-700"
                aria-label="Dismiss trial banner"
              >
                <Icons.X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Page content */}
        <main className="px-4 py-8 sm:px-6 lg:px-8 print:p-0 print:max-w-none">
          {paymentBlocked && !isOnPaymentSafePage ? (
            <div className="max-w-2xl mx-auto mt-8">
              <PaymentPrompt />
            </div>
          ) : (
            <>
              {user && !isGlobalSuperAdmin(user) && userRole === "PATRON" && <PaymentPrompt />}
              {children}
            </>
          )}
        </main>
        <ScrollToTopFab />
      </div>

      {/* Transfer Queue Drawer */}
      <div className="print:hidden">
        <TransferQueueDrawer
          open={transferDrawerOpen}
          onClose={() => setTransferDrawerOpen(false)}
          branches={allBranches}
        />
      </div>
    </div>
  );
}
